// @ts-nocheck
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { BxAppId } from '../config/bxApps';

export type ActivityDraftStatus = 'draft' | 'ready' | 'funded' | 'in_progress' | 'completed';

export interface ActivityDraft {
  id: string;
  appId: BxAppId;
  title: string;
  stepIndex: number;
  formData: Record<string, string>;
  status: ActivityDraftStatus;
  createdAt: string;
  updatedAt: string;
  resumePath: string;
  launchHref?: string;
  notes?: string;
}

const STORAGE_KEY = 'bx_activity_cart';

const readStored = (): ActivityDraft[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeStored = (drafts: ActivityDraft[]) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
};

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

type ActivityCartContextValue = {
  drafts: ActivityDraft[];
  activeCount: number;
  upsertDraft: (draft: Partial<ActivityDraft> & Pick<ActivityDraft, 'appId' | 'title' | 'resumePath'>) => string;
  updateDraft: (id: string, patch: Partial<ActivityDraft>) => void;
  removeDraft: (id: string) => void;
  getDraft: (id: string) => ActivityDraft | undefined;
};

const ActivityCartContext = createContext<ActivityCartContextValue>({
  drafts: [],
  activeCount: 0,
  upsertDraft: () => '',
  updateDraft: () => {},
  removeDraft: () => {},
  getDraft: () => undefined,
});

export const ActivityCartProvider = ({ children }) => {
  const [drafts, setDrafts] = useState(readStored);

  useEffect(() => {
    writeStored(drafts);
  }, [drafts]);

  const upsertDraft = useCallback(
    ({ id, appId, title, resumePath, ...rest }) => {
      const now = new Date().toISOString();
      let resultId = id;

      setDrafts(prev => {
        if (id) {
          const idx = prev.findIndex(d => d.id === id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = {
              ...next[idx],
              ...rest,
              appId,
              title,
              resumePath,
              updatedAt: now,
            };
            return next;
          }
        }

        resultId = resultId || newId();
        const entry: ActivityDraft = {
          id: resultId,
          appId,
          title,
          resumePath,
          stepIndex: rest.stepIndex ?? 0,
          formData: rest.formData ?? {},
          status: rest.status ?? 'draft',
          createdAt: now,
          updatedAt: now,
          launchHref: rest.launchHref,
          notes: rest.notes,
        };
        return [entry, ...prev];
      });

      return resultId || newId();
    },
    []
  );

  const updateDraft = useCallback((id, patch) => {
    setDrafts(prev =>
      prev.map(d =>
        d.id === id ? { ...d, ...patch, updatedAt: new Date().toISOString() } : d
      )
    );
  }, []);

  const removeDraft = useCallback(id => {
    setDrafts(prev => prev.filter(d => d.id !== id));
  }, []);

  const getDraft = useCallback(id => drafts.find(d => d.id === id), [drafts]);

  const activeCount = useMemo(
    () => drafts.filter(d => d.status !== 'completed').length,
    [drafts]
  );

  const value = useMemo(
    () => ({
      drafts,
      activeCount,
      upsertDraft,
      updateDraft,
      removeDraft,
      getDraft,
    }),
    [drafts, activeCount, upsertDraft, updateDraft, removeDraft, getDraft]
  );

  return (
    <ActivityCartContext.Provider value={value}>{children}</ActivityCartContext.Provider>
  );
};

export const useActivityCart = () => useContext(ActivityCartContext);

export default ActivityCartContext;
